// RSA-OAEP wrap of DEK with the escrow public key (D-45, COF-04).
// Phase 1: single-key break-glass. Phase 6: Shamir 3-of-5.
// The escrow public key is bundled in the SPA (JWK); private key NEVER leaves YubiKey/HSM.

// TS 6.x: crypto.subtle requires ArrayBuffer, not ArrayBufferLike.
function toBuffer(arr: Uint8Array): ArrayBuffer {
  return arr.buffer.slice(arr.byteOffset, arr.byteOffset + arr.byteLength) as ArrayBuffer;
}

/** Phase 1 single-key escrow version marker (CRY-05). */
export const ESCROW_KEY_VERSION = 1;

/**
 * RSA-OAEP wrap of DEK with the escrow public key (JWK).
 * Output size depends on RSA modulus (e.g., ~512 bytes for RSA-4096).
 */
export async function wrapDekWithEscrow(
  dekRaw: Uint8Array,
  escrowPublicJwk: JsonWebKey,
): Promise<Uint8Array> {
  const publicKey = await crypto.subtle.importKey(
    'jwk',
    escrowPublicJwk,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['wrapKey'],
  );

  // Import DEK as extractable CryptoKey for wrapping
  const dekKey = await crypto.subtle.importKey(
    'raw',
    toBuffer(dekRaw),
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );

  const wrapped = await crypto.subtle.wrapKey('raw', dekKey, publicKey, {
    name: 'RSA-OAEP',
  });
  return new Uint8Array(wrapped);
}

/**
 * RSA-OAEP unwrap of DEK with the escrow private key.
 * Phase 1: used in round-trip tests only.
 * Phase 6: used in production break-glass with the actual YubiKey-backed private key (D-45).
 */
export async function unwrapDekWithEscrow(
  wrappedDek: Uint8Array,
  escrowPrivateJwk: JsonWebKey,
): Promise<Uint8Array> {
  const privateKey = await crypto.subtle.importKey(
    'jwk',
    escrowPrivateJwk,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['unwrapKey'],
  );

  const dekKey = await crypto.subtle.unwrapKey(
    'raw',
    toBuffer(wrappedDek),
    privateKey,
    { name: 'RSA-OAEP' },
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );

  const exported = await crypto.subtle.exportKey('raw', dekKey);
  return new Uint8Array(exported);
}
